import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthCard } from '@/pages/auth/AuthCard';
import { PasswordInput } from '@/components/PasswordInput';
import { useConfirmPasswordResetMutation } from '@/services/api';
import { checkPasswordPolicy, isWellFormedResetToken } from '@/utils/auth';
import { parseApiError } from '@/utils/apiError';

export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [confirmReset, { isLoading }] = useConfirmPasswordResetMutation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const tokenValid = isWellFormedResetToken(token);
  const pwPolicy = checkPasswordPolicy(password);
  const passwordsMatch = confirm === '' || password === confirm;
  const canSubmit = pwPolicy.ok && confirm !== '' && password === confirm && !isLoading;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setServerError(null);
    try {
      await confirmReset({ token: token as string, new_password: password }).unwrap();
      navigate('/login', { replace: true, state: { banner: 'Password reset successfully — please sign in' } });
    } catch (err) {
      setServerError(parseApiError(err).message);
    }
  }

  if (!tokenValid) {
    return (
      <AuthCard>
        <h1 className="title">Reset link problem</h1>
        <div className="invalid-card" role="alert">
          This password reset link is invalid or has expired.
        </div>
        <p className="auth-foot">
          <Link to="/forgot-password">Request a new reset link →</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <h1 className="title">Set a new password</h1>
      <p className="subtitle">Choose a new password for your account.</p>
      {serverError && (
        <div className="invalid-card" role="alert">
          {serverError}{' '}
          <Link to="/forgot-password" style={{ borderBottom: '1px solid var(--text)', fontWeight: 500 }}>
            Request a new reset link
          </Link>
        </div>
      )}
      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="rp-password">
            New password
          </label>
          <PasswordInput
            allowAutofill
            id="rp-password"
            invalid={password !== '' && !pwPolicy.ok}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={password}
            onChange={setPassword}
          />
          {password !== '' && !pwPolicy.ok ? (
            <div className="field-err">{pwPolicy.reason}</div>
          ) : (
            <div className="field-help">Minimum 12 characters, mixed character classes.</div>
          )}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rp-confirm">
            Confirm new password
          </label>
          <PasswordInput
            allowAutofill
            id="rp-confirm"
            invalid={!passwordsMatch}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={confirm}
            onChange={setConfirm}
          />
          {!passwordsMatch && <div className="field-err">Passwords do not match.</div>}
        </div>
        <button className="btn-primary" type="submit" disabled={!canSubmit}>
          {isLoading ? 'Saving…' : 'Set password'}
        </button>
      </form>
      <p className="auth-foot">
        <Link to="/login">← Back to sign in</Link>
      </p>
    </AuthCard>
  );
}
